import { apiToken } from '../config/ApiService';

function extractErrorMessage(error: any, fallback: string): string {
  return error?.response?.data?.detail || fallback;
}

interface User {
  id: number;
  name: string;
  email: string;
  is_active: boolean;
  group_id: number;
  group_name: string;
  created_on: string;
  updated_on: string;
  created_by_name: string;
  updated_by_name: string;
}

interface Group {
  id: number;
  name: string;
}

export interface PricingVersion {
  id: number;
  name: string;
  created_on: string;
}

export interface Region {
  region_id: number;
  region_code: string;
}

// --- Base price version upload (validate / commit) ---

export interface RowError {
  row_number: number;
  resource_type?: string | null;
  region?: string | null;
  service_line?: string | null;
  function?: string | null;
  career_level?: string | null;
  message: string;
}

export interface ChangedField {
  field: string;
  old: any;
  new: any;
}

export interface BasePriceRowValues {
  cost_per_hour_usd?: number | null;
  cost_per_hour_gbp?: number | null;
  cost_per_hour_eur?: number | null;
  burden_load_rate?: number | null;
  target_client_margin?: number | null;
  min_fte_allocation?: number | null;
  cost_per_hour_editable?: boolean | null;
  target_client_margin_editable?: boolean | null;
}

export interface BasePriceDiffRow {
  resource_type?: string | null;
  region?: string | null;
  service_line?: string | null;
  function?: string | null;
  career_level?: string | null;
  changed_fields: ChangedField[];
  values?: BasePriceRowValues | null;
}

export interface BasePriceSummary {
  added: BasePriceDiffRow[];
  edited: BasePriceDiffRow[];
  deleted: BasePriceDiffRow[];
  added_count: number;
  edited_count: number;
  deleted_count: number;
}

export interface BasePriceValidationResponse {
  is_valid: boolean;
  file_sha256: string;
  header_errors: string[];
  row_errors: RowError[];
  summary: BasePriceSummary | null;
  compared_currencies: boolean;
  ccr_changed: boolean;
  default_version_name: string;
  default_usd_to_eur_ccr: number | null;
  default_usd_to_gbp_ccr: number | null;
  prev_usd_to_eur_ccr: number | null;
  prev_usd_to_gbp_ccr: number | null;
  line_item_type: number | null;
  prev_version_id: number | null;
  prev_version_name: string | null;
}

export interface BasePriceDefaults {
  default_version_name: string;
  prev_version_id: number;
  prev_version_name: string;
  prev_usd_to_eur_ccr: number | null;
  prev_usd_to_gbp_ccr: number | null;
  line_item_type: number | null;
}

export interface BasePriceCommitResponse {
  version_id: number;
  version_name: string;
  summary: BasePriceSummary | null;
}

export interface CommitBasePricePayload {
  version_name: string;
  usd_to_eur_ccr: number;
  usd_to_gbp_ccr: number;
  file_sha256: string;
}

interface EditUserPayload {
  id: number;
  name: string;
  email: string;
  is_active: boolean;
  group_id: number;
}

interface CreateUserPayload {
  name: string;
  email: string;
  is_active: boolean;
  group_id: number;
}

export const adminService = {
  getAllUsers: async (): Promise<User[]> => {
    try {
      const response = await apiToken.get('/auth/users');
      if (response && response.data) {
        return response.data;
      }
      throw new Error("Response is undefined or doesn't contain data");
    } catch (error: any) {
      console.error('Error Response Data:', error?.response?.data || error.message);
      console.error('Error Response Status:', error?.response?.status || "No status available");
      throw new Error(extractErrorMessage(error, 'Failed to fetch users'));
    }
  },

  getUser: async (userId: number): Promise<User> => {
    try {
      const response = await apiToken.get(`/auth/users/${userId}`);
      if (response && response.data) {
        return response.data;
      }
      throw new Error("Response is undefined or doesn't contain data");
    } catch (error: any) {
      console.error('Error Response Data:', error?.response?.data || error.message);
      console.error('Error Response Status:', error?.response?.status || "No status available");
      throw new Error(extractErrorMessage(error, 'Failed to fetch user details'));
    }
  },

  editUser: async (payload: EditUserPayload): Promise<void> => {
    try {
      await apiToken.post('/auth/edit_user', payload);
    } catch (error: any) {
      console.error('Error Response Data:', error?.response?.data || error.message);
      console.error('Error Response Status:', error?.response?.status || "No status available");
      throw new Error(extractErrorMessage(error, 'Failed to update user'));
    }
  },

  getActiveGroups: async (): Promise<Group[]> => {
    try {
      const response = await apiToken.get('/auth/active_groups');
      if (response && response.data) {
        return response.data;
      }
      throw new Error("Response is undefined or doesn't contain data");
    } catch (error: any) {
      console.error('Error Response Data:', error?.response?.data || error.message);
      console.error('Error Response Status:', error?.response?.status || "No status available");
      throw new Error(extractErrorMessage(error, 'Failed to fetch groups'));
    }
  },

  addUser: async (payload: CreateUserPayload): Promise<void> => {
    try {
      await apiToken.post('/auth/add_user', payload);
    } catch (error: any) {
      console.error('Error Response Data:', error?.response?.data || error.message);
      console.error('Error Response Status:', error?.response?.status || "No status available");
      throw new Error(extractErrorMessage(error, 'Failed to create user'));
    }
  },

  getPricingVersions: async (): Promise<PricingVersion[]> => {
    try {
      const response = await apiToken.get('/admin/pricing-versions');
      if (response && response.data) {
        return response.data;
      }
      throw new Error("Response is undefined or doesn't contain data");
    } catch (error: any) {
      console.error('Error Response Data:', error?.response?.data || error.message);
      console.error('Error Response Status:', error?.response?.status || "No status available");
      throw new Error(extractErrorMessage(error, 'Failed to fetch pricing versions'));
    }
  },

  getRegions: async (): Promise<Region[]> => {
    try {
      const response = await apiToken.get('/admin/regions');
      if (response && response.data) {
        return response.data;
      }
      throw new Error("Response is undefined or doesn't contain data");
    } catch (error: any) {
      console.error('Error Response Data:', error?.response?.data || error.message);
      console.error('Error Response Status:', error?.response?.status || "No status available");
      throw new Error(extractErrorMessage(error, 'Failed to fetch regions'));
    }
  },

  getBasePriceVersionDefaults: async (): Promise<BasePriceDefaults> => {
    try {
      const response = await apiToken.get('/admin/base-price-version/defaults');
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error, 'Failed to load base price version defaults'));
    }
  },

  validateBasePriceVersion: async (
    file: File,
    ccrs?: { usd_to_eur_ccr?: number; usd_to_gbp_ccr?: number; compare_currencies?: boolean },
  ): Promise<BasePriceValidationResponse> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (ccrs?.usd_to_eur_ccr != null) formData.append('usd_to_eur_ccr', String(ccrs.usd_to_eur_ccr));
      if (ccrs?.usd_to_gbp_ccr != null) formData.append('usd_to_gbp_ccr', String(ccrs.usd_to_gbp_ccr));
      if (ccrs?.compare_currencies != null) formData.append('compare_currencies', String(ccrs.compare_currencies));
      const response = await apiToken.post('/admin/base-price-version/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error, 'Failed to validate base price file'));
    }
  },

  commitBasePriceVersion: async (
    file: File,
    payload: CommitBasePricePayload,
  ): Promise<BasePriceCommitResponse> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('version_name', payload.version_name);
      formData.append('usd_to_eur_ccr', String(payload.usd_to_eur_ccr));
      formData.append('usd_to_gbp_ccr', String(payload.usd_to_gbp_ccr));
      formData.append('file_sha256', payload.file_sha256);
      const response = await apiToken.post('/admin/base-price-version/commit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error, 'Failed to create base price version'));
    }
  },
}; 